import { useEffect, useState } from 'react';
import { Button, Table, Tag, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { t } from '@/utils/translator';
import { StatusColor, CycleStatus } from '@constants/status';
import AttachmentCell from './AttachmentCell';
import CycleActionCell from './CycleActionCell';
import FeedbackTable from './FeedbackTable';
import i18n from '@/i18n';

const { Title } = Typography;
const { Column, ColumnGroup } = Table;

const EstimateCycleTable = ({
  EstimateCycle,
  onScope3TemplateModal,
  RequiredDocuments = [],
  OptionalDocuments = [],
  refreshCycleData,
  onDownloadTemplate,
}) => {
  const [AttachmentColumnWidth, setAttachmentColumnWidth] = useState(0);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    const _AttachmentColumnWidth =
      RequiredDocuments.length + OptionalDocuments.length
        ? `${70 / (RequiredDocuments.length + OptionalDocuments.length)}%`
        : !OptionalDocuments.length
          ? `${70 / RequiredDocuments.length}%`
          : `${70 / OptionalDocuments.length}%`;

    setAttachmentColumnWidth(_AttachmentColumnWidth);
  }, [RequiredDocuments, OptionalDocuments, setAttachmentColumnWidth]);

  // cycle_index 기준으로 내림차순 정렬 (큰 숫자부터 작은 순서)
  const sortedEstimateCycle = [...(EstimateCycle || [])].sort(
    (a, b) => b.cycle_index - a.cycle_index
  );

  // API에서 받은 문서 리스트에서 키 추출 (새로운 API 구조에 맞게 수정)
  const RequiredProjectAttachements = RequiredDocuments.map((doc) => doc.document_type || doc.key);
  const OptionalProjectAttachements = OptionalDocuments.map((doc) => doc.document_type || doc.key);

  const hasAnyFeedback = (cycle) => {
    if (cycle.feedback) return true;
    // attachment_infos 배열에서 피드백 존재 여부 확인
    if (cycle.attachment_infos && Array.isArray(cycle.attachment_infos)) {
      return cycle.attachment_infos.some((attachment_info) => attachment_info.feedback);
    }
    return false;
  };

  return (
    <>
      <div className="mb-2">
        <Title level={4} className="text-dark mb-0">
          {t('산정 증빙자료 제출')}
        </Title>
      </div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex justify-content-end">
          <div className="fw-medium me-2" style={{ fontSize: '15px' }}>
            {t('회차별 상태')}:
          </div>
          {Object.entries(StatusColor).map(([state]) => (
            <Tag color={StatusColor[state]} key={state}>
              {t(CycleStatus[state])}
            </Tag>
          ))}
        </div>
        <div>
          <Button className="me-2" onClick={() => onScope3TemplateModal(true)}>
            {t('품번조회')}
          </Button>
          {/* <Button icon={<DownloadOutlined />} onClick={onDownloadTemplate}>
            {t('협력사 온실가스 배출량 수집 템플릿 다운로드')}
          </Button> */}
        </div>
      </div>
      <Table
        dataSource={sortedEstimateCycle}
        rowKey="cycle_index"
        pagination={{ position: ['none', 'none'] }}
        bordered
        style={{ width: '100%' }}
        tableLayout="fixed"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: setExpandedRowKeys,
          expandedRowRender: (record) => {
            // attachment_infos 배열에서 피드백이 있는 항목들 추출
            const feedbackRows = (record.attachment_infos || [])
              .filter((attachment_info) => attachment_info?.feedback && attachment_info.feedback)
              .map((attachment_info) => ({
                key: attachment_info.id,
                attachment_type: attachment_info.document_type,
                filename: attachment_info.attachment?.filename || '-',
                feedback: attachment_info.feedback,
              }));

            return <FeedbackTable row={record} feedbacks={feedbackRows} />;
          },
          rowExpandable: (record) => hasAnyFeedback(record),
          showExpandColumn: false,
        }}
      >
        <Column
          title={<span className={clsx('fw-medium', 'fs-6')}>{t('회차')}</span>}
          dataIndex="cycle_index"
          key="cycle_index"
          align="center"
          width={'10%'}
          render={(cycle_index) => <span style={{ fontSize: '15px' }}>{cycle_index}</span>}
        />
        <Column
          title={<span className={clsx('fw-medium', 'fs-6')}>{t('상태')}</span>}
          dataIndex="cycle_status"
          key="cycle_status"
          align="center"
          width={'10%'}
          render={(cycle_status, record) => (
            <Tag color={StatusColor[record.cycle_status]} key={cycle_status}>
              {t(CycleStatus[cycle_status])}
            </Tag>
          )}
        />
        {RequiredDocuments.length > 0 && (
          <ColumnGroup
            title={<span className={clsx('fw-medium', 'fs-6')}>{t('필수 증빙서류')}</span>}
            className="text-center"
          >
            {RequiredDocuments.map((document) => (
              <Column
                key={document.id || document.document_type}
                title={
                  <span className={clsx('fw-medium', '')}>
                    {document[`type_name_${i18n.language}`] ||
                      document.type_name_ko ||
                      document.document_type}
                  </span>
                }
                dataIndex={document.document_type || document.key}
                align="center"
                render={(_, record) => {
                  // 해당 document_type_id에 맞는 첨부파일만 필터링
                  const documentTypeId = document.document_type_id || document.id;
                  const documentType = document.document_type || document.key;
                  const filteredAttachments = record.attachment_infos
                    ? record.attachment_infos.filter(
                        (att_info) => att_info.document_type_id === documentTypeId
                      )
                    : [];
                  return (
                    <AttachmentCell
                      attachments={filteredAttachments}
                      record={record}
                      documentType={documentType}
                      refreshCycleData={refreshCycleData}
                    />
                  );
                }}
                width={AttachmentColumnWidth}
                ellipsis
              />
            ))}
          </ColumnGroup>
        )}

        {OptionalDocuments.length > 0 && (
          <ColumnGroup
            title={<span className={clsx('fw-medium', 'fs-6')}>{t('증빙서류')}</span>}
            className="text-center"
          >
            {OptionalDocuments.map((document) => (
              <Column
                key={document.id || document.document_type}
                title={
                  <span className={clsx('fw-medium', '')}>
                    {document[`type_name_${i18n.language}`] ||
                      document.type_name_ko ||
                      document.document_type}
                  </span>
                }
                dataIndex={document.document_type || document.key}
                align="center"
                render={(_, record) => {
                  // 해당 document_type_id에 맞는 첨부파일만 필터링
                  const documentTypeId = document.document_type_id || document.id;
                  const documentType = document.document_type || document.key;
                  const filteredAttachments = record.attachment_infos
                    ? record.attachment_infos.filter(
                        (att_info) => att_info.document_type_id === documentTypeId
                      )
                    : [];
                  return (
                    <AttachmentCell
                      attachments={filteredAttachments}
                      record={record}
                      documentType={documentType}
                      refreshCycleData={refreshCycleData}
                    />
                  );
                }}
                width={AttachmentColumnWidth}
                ellipsis
              />
            ))}
          </ColumnGroup>
        )}
        <Column
          title={<span className={clsx('fw-medium', 'fs-6')}>{t('제출하기')}</span>}
          dataIndex="action"
          key="action"
          align="center"
          render={(_, record) => (
            <CycleActionCell
              record={record}
              onExpandRow={setExpandedRowKeys}
              RequiredDocuments={RequiredDocuments}
              OptionalDocuments={OptionalDocuments}
              refreshCycleData={refreshCycleData}
            />
          )}
          width={'10%'}
        />
      </Table>
    </>
  );
};

export default EstimateCycleTable;
